/**
 * 🎮 USE GAMES - Hook pour les mini-jeux
 * Architecture Mozart/Jobs/Tesla
 */

import { useState, useCallback } from 'react';
import { api } from '../../services/api';
import { toast } from 'sonner';

export const useGames = (customerId) => {
  // État
  const [isLoading, setIsLoading] = useState(false);
  const [currentGame, setCurrentGame] = useState(null); // 'would-you-rather' | 'quiz' | 'truth-or-dare'
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [gameResult, setGameResult] = useState(null);

  // Question actuelle
  const currentQuestion = questions[currentQuestionIndex] || null;
  const isLastQuestion = currentQuestionIndex >= questions.length - 1;

  // ============================================
  // WOULD YOU RATHER
  // ============================================
  const startWouldYouRather = useCallback(async () => {
    setIsLoading(true);
    setCurrentGame('would-you-rather');
    setAnswers({});
    setCurrentQuestionIndex(0);
    setGameResult(null);
    
    try {
      const response = await api.getGamesWouldYouRather?.() || 
                       await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/games/would-you-rather/questions`);
      const data = response.data || await response.json();
      
      if (data.success && data.questions) {
        setQuestions(data.questions);
      } else {
        throw new Error('No questions received');
      }
    } catch (err) {
      console.error('useGames startWouldYouRather error:', err);
      toast.error('Erreur lors du chargement des questions');
      setCurrentGame(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ============================================
  // QUIZ COMPATIBILITÉ
  // ============================================
  const startCompatibilityQuiz = useCallback(async () => {
    setIsLoading(true);
    setCurrentGame('quiz');
    setAnswers({});
    setCurrentQuestionIndex(0);
    setGameResult(null);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/games/compatibility-quiz/questions`);
      const data = await response.json();
      
      if (data.success && data.questions) {
        setQuestions(data.questions);
      } else {
        throw new Error('No questions received');
      }
    } catch (err) {
      console.error('useGames startCompatibilityQuiz error:', err);
      toast.error('Erreur lors du chargement du quiz');
      setCurrentGame(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ============================================
  // TRUTH OR DARE
  // ============================================
  const getTruthOrDare = useCallback(async (type = 'truth') => {
    setIsLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/games/truth-or-dare/${type}`);
      const data = await response.json();
      
      if (data.success) {
        return {
          type: data.type,
          question: data.question,
          emoji: data.emoji
        };
      }
      return null;
    } catch (err) {
      console.error('useGames getTruthOrDare error:', err);
      toast.error('Erreur lors du chargement');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ============================================
  // ACTIONS
  // ============================================
  const answerQuestion = useCallback((questionId, answer) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  }, []);

  const nextQuestion = useCallback(() => {
    if (!isLastQuestion) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  }, [isLastQuestion]);

  const previousQuestion = useCallback(() => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  }, [currentQuestionIndex]);

  const submitGame = useCallback(async () => {
    if (!currentGame) return;
    
    setIsLoading(true);
    try {
      // Envoyer les réponses au backend
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/games/${currentGame}/submit?customer_id=${customerId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ answers })
        }
      );
      const data = await response.json();
      
      if (data.success) {
        setGameResult(data.result);
        toast.success('Quiz terminé ! 🎉');
      }
    } catch (err) {
      console.error('useGames submitGame error:', err);
      toast.error('Erreur lors de la soumission');
    } finally {
      setIsLoading(false);
    }
  }, [currentGame, answers, customerId]);

  const resetGame = useCallback(() => {
    setCurrentGame(null);
    setQuestions([]);
    setCurrentQuestionIndex(0);
    setAnswers({});
    setGameResult(null);
  }, []);

  return {
    // État
    isLoading,
    currentGame,
    questions,
    currentQuestion,
    currentQuestionIndex,
    isLastQuestion,
    answers,
    gameResult,
    
    // Actions
    startWouldYouRather,
    startCompatibilityQuiz,
    getTruthOrDare,
    answerQuestion,
    nextQuestion,
    previousQuestion,
    submitGame,
    resetGame
  };
};

export default useGames;
